import DesignButton, {
  DesignButtonSize,
  DesignButtonVariant,
} from 'components/button/DesignButton';
import DesignDialog from 'components/dialog/DesignDialog';
import { CapacityPlanRunStatus } from 'core/graphql/types';
import { useState } from 'react';
import { isRunSolving } from './runProgress';

export const geocodeButtonLabel = 'Geocode missing coordinates';

/**
 * Asked every time rather than remembered: the request costs money per row and
 * the count changes between two looks at the same Run.
 */
export const geocodeConfirmation = (missingCoordinates: number) =>
  `Send ${missingCoordinates} rows to address processing? This uses paid geocoding and can take a while.`;

export interface GeocodeButtonProps {
  status: CapacityPlanRunStatus;
  /** Valid Deliveries still without a pin; nothing is offered when this is 0. */
  missingCoordinates: number;
  canEdit: boolean;
  isRequesting: boolean;
  onConfirm: () => void;
  variant?: DesignButtonVariant;
  size?: DesignButtonSize;
}

/**
 * "Geocode missing coordinates" plus the confirmation it always asks for.
 */
const GeocodeButton = ({
  status,
  missingCoordinates,
  canEdit,
  isRequesting,
  onConfirm,
  variant,
  size,
}: GeocodeButtonProps) => {
  const [isConfirming, setIsConfirming] = useState(false);

  if (!canEdit || missingCoordinates === 0) {
    return null;
  }

  // A Run whose Solves are in flight would have the geocoding request
  // refused by the API, so it is not offered.
  const isBusy = isRunSolving(status);

  const confirm = () => {
    setIsConfirming(false);
    onConfirm();
  };

  return (
    <>
      <DesignButton
        variant={variant}
        size={size}
        disabled={isBusy || isRequesting}
        onClickHandler={() => setIsConfirming(true)}
      >
        {geocodeButtonLabel} ({missingCoordinates})
      </DesignButton>

      <DesignDialog
        open={isConfirming}
        onClose={() => setIsConfirming(false)}
        labelledBy="geocodeConfirmTitle"
        className="w-[480px]"
      >
        <div className="flex flex-col gap-4 p-5">
          <h3 id="geocodeConfirmTitle" className="text-xl font-bold">
            {geocodeButtonLabel}
          </h3>
          <p className="text-sm">{geocodeConfirmation(missingCoordinates)}</p>
          <div className="flex justify-end gap-2">
            <DesignButton
              variant="ghost"
              onClickHandler={() => setIsConfirming(false)}
            >
              Cancel
            </DesignButton>
            <DesignButton variant="primary" onClickHandler={confirm}>
              Send to geocoding
            </DesignButton>
          </div>
        </div>
      </DesignDialog>
    </>
  );
};

GeocodeButton.defaultProps = {
  variant: 'secondary',
  size: 'md',
};

export default GeocodeButton;
